<cs-card-list (placementsChanged)="onCardPlacementsChanged($event)">
  <!-- Projected header cards (info cards, warnings, etc.) -->
  <ng-content select="[header-cards]"></ng-content>

  <!-- Insert row for edit mode -->
  @if (editable) {
  <cs-card-list-row class="insert-row" (click)="onInsertRowClick($event)">
    <div class="insert-row-line"></div>
    <cs-button secondary small icon="add"></cs-button>
    <div class="insert-row-line"></div>
  </cs-card-list-row>
  }

  @for (item of editListItems; track trackByEditListItem(i, item); let i = $index) {
  <!-- Edit mode: non-foldable with drag handle -->
  @if (editable) {
  <cs-card class="exercise-card" [attr.id]="item.exercise.exercise._id" [attr.data-index]="i" [foldable]="false"
    [class.dragging]="dragService.isDragging(i)"
    [class.combo-set-first]="getComboSetPosition(item.exercise) === 'first'"
    [class.combo-set-middle]="getComboSetPosition(item.exercise) === 'middle'"
    [class.combo-set-last]="getComboSetPosition(item.exercise) === 'last'"
    hoverable tappable (pointerdown)="onEditItemLongPress($event, i)" (click)="onHeaderClick(item.exercise)">
    <div class="combo-set-overlay">
      <span>{{ LL.workouts.detail.superset() }}</span>
    </div>
    <cs-card-title class="exercise-card-header">
      <div class="exercise-header-row">
        <div class="drag-zone" (pointerdown)="onEditItemDragStart($event, item, i)"
          (click)="$event.stopPropagation()">
          <cs-icon name="drag-indicator" class="drag-handle"></cs-icon>
          <cs-exercise-status-badge [type]="getBadgeType(item.exercise)" [number]="getExerciseNumber(item.exercise)" [filled]="fillCompletedBadge">
          </cs-exercise-status-badge>
        </div>
        <div class="exercise-info">
          <div class="exercise-title-description">
            <div class="exercise-text">
              <div class="exercise-title">{{ item.exercise.exercise.name }}</div>
              @if (item.exercise.editDescription) {
              <div class="exercise-description edit-mode">
                <span>{{ item.exercise.editDescription }}</span>
                <span><cs-icon name="arrow-dropright" class="chevron-icon"></cs-icon></span>
              </div>
              }
            </div>
            @if (showMoreButton) {
            <div class="collapsed-controls">
              <cs-icon-button icon="ellipsis-vertical"
                (click)="onMoreOptionsClick($event, item.exercise)"></cs-icon-button>
            </div>
            }
          </div>
        </div>
      </div>
    </cs-card-title>
  </cs-card>
  }
  <!-- Read-only mode: foldable with expanded state -->
  @if (!editable) {
  <cs-card class="exercise-card" [attr.id]="item.exercise.exercise._id" [attr.data-sub-slot-id]="item.exercise.logExercise?.subSlotId" [foldable]="foldable"
    [expanded]="foldable && isExpanded(item.exercise)" (expandedChange)="onExpandedChange(item.exercise, $event)"
    [hideFoldIconWhenExpanded]="hideExpandedFoldIcon"
    [highlighted]="isHighlighted(item.exercise)" (highlightedChange)="onHighlightedChange(item.exercise, $event)"
    [hideHighlightOutline]="hideHighlightOutline"
    [loading]="isLoading(item.exercise)" (headerClick)="onHeaderClick(item.exercise)"
    [attr.hoverable]="!foldable ? '' : null" [attr.tappable]="!foldable ? '' : null"
    [class.done]="item.exercise.isCompleted()" [class.incomplete]="item.exercise.isIncomplete()"
    [class.skipped]="item.exercise.isSkipped()"
    [class.combo-set-first]="getComboSetPosition(item.exercise) === 'first'"
    [class.combo-set-middle]="getComboSetPosition(item.exercise) === 'middle'"
    [class.combo-set-last]="getComboSetPosition(item.exercise) === 'last'">
    <cs-card-title class="exercise-card-header">
      <div class="exercise-header-row">
        <cs-exercise-status-badge [type]="getBadgeType(item.exercise)" [number]="getExerciseNumber(item.exercise)" [filled]="fillCompletedBadge">
        </cs-exercise-status-badge>
        <div class="exercise-info">
          <div class="exercise-title-description">
            <div class="exercise-text">
              <div class="exercise-title">{{ item.exercise.exercise.name }}</div>
              @if (hasCustomDescription(item.exercise)) {
              <!-- #4142 review fixes: the parent draws this line (a finished exercise's done sets and chip) -->
              <div class="exercise-description custom">
                <ng-container
                  *ngTemplateOutlet="descriptionTemplate; context: {$implicit: item.exercise, exercise: item.exercise}"></ng-container>
              </div>
              } @else if (item.exercise.description) {
              <div class="exercise-description">
                <span>{{ item.exercise.description }}</span>
                @if (item.exercise.hasPeriodizationDescription) {
                <help-icon name="info" size="x-small" helpId="@help:references.periodization-intensity"></help-icon>
                }
              </div>
              }
            </div>
            @if (showMoreButton && (!foldable || !isExpanded(item.exercise))) {
            <div class="collapsed-controls">
              <cs-icon-button icon="ellipsis-vertical"
                (click)="onMoreOptionsClick($event, item.exercise)"></cs-icon-button>
            </div>
            } @else if (showExpandedMoreButton && foldable && isExpanded(item.exercise)) {
            <!-- #4142: the expanded card's exercise menu, top right (mock boards H/J). The id is the
              one the bar's More button carried, which the E2E specs click -->
            <div class="collapsed-controls">
              <cs-icon-button id="exerciseMenu" icon="ellipsis-vertical"
                (click)="onExpandedMoreClick($event, item.exercise)"></cs-icon-button>
            </div>
            }
          </div>
          @if (item.exercise.badges?.length > 0) {
          <div class="exercise-badges">
          	@for (badge of item.exercise.badges; track badge.label) {
            <cs-badge [label]="badge.label" [color]="badge.color">
              @if (badge.info) {
              <cs-tooltip [title]="badge.label">
                {{ badge.info }}
                @if (badge.helpId) {
                <help-link [helpId]="badge.helpId">{{ LL.common.buttons.learnMore() }}</help-link>
                }
              </cs-tooltip>
              }
            </cs-badge>
            }
          </div>
          }
        </div>
      </div>
    </cs-card-title>
    @if (collapsedTemplate && !isExpanded(item.exercise)) {
    <div collapsed>
      <ng-container
        *ngTemplateOutlet="collapsedTemplate; context: {$implicit: item.exercise, exercise: item.exercise}"></ng-container>
    </div>
    }
    @if (expandedTemplate) {
    <div expanded>
      <ng-container
        *ngTemplateOutlet="expandedTemplate; context: {$implicit: item.exercise, exercise: item.exercise}"></ng-container>
    </div>
    }
  </cs-card>
  }
  }

  <!-- Projected footer cards (completion cards, etc.) -->
  <ng-content select="[footer-cards]"></ng-content>
</cs-card-list>

<!-- Insert indicators (desktop edit mode only) -->
@if (editable && !dragService.isAnyDragActive()) {
<div class="insert-indicators-container">
  @for (i of getInsertIndicatorIndices(); track i) {
  @if (insertZoneTops()[i] !== undefined) {
  <div class="insert-zone" [style.top.px]="insertZoneTops()[i]" (mouseenter)="onInsertZoneHover(i, true)"
    (mouseleave)="onInsertZoneHover(i, false)" (click)="onInsertIndicatorClick($event, i)">
    <div class="insert-indicator" [class.visible]="hoveredInsertIndex === i">
      <cs-button secondary x-small icon="add" hover></cs-button>
      <div class="insert-line"></div>
    </div>
  </div>
  }
  }
</div>
}
